/* eslint-disable no-unused-vars */
import React, { lazy, Suspense, useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, doc, getDocs, serverTimestamp, setDoc, updateDoc, waitForPendingWrites } from "firebase/firestore";
import { auth, db } from "./firebase";
import { BranchProvider } from "./context/BranchContext";

const AppRoutes = lazy(() => import('./routes/AppRoutes'));
const ToastVisualQaHarness = process.env.NODE_ENV !== 'production'
  ? require('./pages/ToastVisualQaHarness').default
  : null;

import { getCurrentWeekTechnician, getDisplayName, getTechnicianList, setCurrentWeekTechnician } from "./utils/weekTechnician";
import { useSound } from "./utils/sound";
import { logger } from "./utils/dataIsolationLogger";
import { canonicalizeVestigingId, resolveVestigingIdWithData, resolveVestigingDocId, getVestigingIdCandidates } from "./utils/resolveVestigingId";
import { countBranchData as countBranchDataShared } from './utils/branchDataCounts';
import { logRouteDebug } from "./utils/routeDebug";
import RouteDebugOverlay from './components/RouteDebugOverlay';
import { logBootEvent } from './init/bootLogger';
import { logAuditEvent, AuditActions, createChangesObject } from './utils/auditLog';
import { sendBrowserNotification } from "./utils/notifications";
import Toast from './assets/Toast';
import CookieBanner from './components/CookieBanner';
import DevelopmentBanner from './components/DevelopmentBanner';
import LoadingScreen from './components/LoadingScreen';
import { logPartsUsage } from "./utils/partsAnalytics";
import { getAndroidUpdateManifestUrl, isNewerAndroidBuild } from "./utils/androidUpdate";
import { getCanonicalRobotName } from "./utils/robotNaming";
import './App.css';
// import krinkelsLogo from './assets/Krinkels-logo.png';
import belRoboticsLogo from './assets/belrobotics-logo.png';
import Login from './components/Login';
import PublicTicketsPage from './features/tickets/PublicTicketsPage';
import useAppAuthContext from './hooks/useAppAuthContext';
import useAppNotifications from './hooks/useAppNotifications';
import useAppVoiceAssistant from './hooks/useAppVoiceAssistant';
import AppHeader from './features/app/AppHeader';
import ApprovalPendingScreen from './features/app/ApprovalPendingScreen';
import PermissionPrompt from './features/app/PermissionPrompt';
import AndroidUpdatePrompt from './features/app/AndroidUpdatePrompt';
import ChangelogUpdatesPrompt from './features/app/ChangelogUpdatesPrompt';
import NotificationPopup from './features/app/NotificationPopup';
import MaintenanceGroundTruthModal from './features/cycle-intelligence/components/MaintenanceGroundTruthModal';
import changelogManifest from './documentatie/changelog.json';
import {
  CURRENT_ANDROID_VERSION,
  CURRENT_ANDROID_VERSION_CODE,
  HUSQVARNA_LOGO as husqvarnaLogo,
  KRESS_LOGO as kressLogo,
  PUBLIC_LOGO as publicLogo,
} from './features/app/appConfig';
import {
  normalizeNotificationPreferences,
} from './features/app/appNotificationHelpers';
import { exportMeldingenCsv, showAppToast } from './features/app/appUiHelpers';
import {
  UPDATE_STATES,
  applyUpdatePrompt,
  compareUpdateVersions,
  dismissUpdatePrompt,
  getChangelogUpdateDecision,
  hasNewerPublishedRelease,
  getUnseenChangelogEntries,
} from './features/app/updatePromptController';
import {
  loadBijzonderhedenData,
  loadGarantiesData,
  loadAnalyticsData,
  loadAnalyticsSelectorData,
  loadRobotPerformanceData,
  loadMagazijnVoorraadData,
  loadMeldingenData,
  loadOnderdelenData,
} from './features/app/appDataLoaders';
import { getAppRouteDataLoadPlan } from './features/app/appRouteDataLoading';
import { invalidateAdminMeldingenCache } from './features/admin/adminMeldingenCache';
import {
  createMeldingEntry,
  deleteMeldingEntry,
  toggleNotificationPreferenceSetting,
  toggleNotificationsSetting,
  updateMeldingStatusEntry,
} from './features/app/appMeldingActions';
import {
  prepareMaintenanceGroundTruthContext,
  saveMaintenanceGroundTruth,
  shouldPromptMaintenanceGroundTruth,
} from './features/cycle-intelligence/services/maintenanceTrainingService';
import {
  downloadLatestAndroidApkFlow,
  fetchAndroidUpdateFlow,
  installAndroidUpdateFlow,
} from './features/app/appAndroidHelpers';
import HomePage from './pages/HomePage';
import './features/app/appShell.css';
import { recordRobotRoundEvent } from './features/robot-round-beta/robotRoundEvents';
import { branchCollectionPath, branchDocumentPath } from './utils/branchScopeResolver';
import { countUnreadHumanMeldingen } from './utils/meldingBadge';

const CHANGELOG_MAX_PROMPT_ITEMS = 3;
const SESSION_IDLE_LIMIT_MS = 10 * 60 * 60 * 1000;
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 60 * 1000;

function compareReleaseVersions(left = '', right = '') {
  const leftParts = String(left)
    .split('.')
    .map((segment) => Number.parseInt(segment, 10));
  const rightParts = String(right)
    .split('.')
    .map((segment) => Number.parseInt(segment, 10));

  const length = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < length; i += 1) {
    const leftPart = Number.isFinite(leftParts[i]) ? leftParts[i] : 0;
    const rightPart = Number.isFinite(rightParts[i]) ? rightParts[i] : 0;
    if (leftPart !== rightPart) {
      return leftPart - rightPart;
    }
  }
  return 0;
}

function parseTimestampToMillis(value) {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isFinite(date?.getTime?.()) ? date.getTime() : 0;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value?.seconds === 'number') return Math.floor(value.seconds * 1000);
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function parseReleaseDateToMillis(dateValue) {
  const raw = String(dateValue || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(`${raw}T23:59:59.999Z`);
  if (Number.isFinite(parsed)) return parsed;
  const fallback = Date.parse(raw);
  return Number.isFinite(fallback) ? fallback : 0;
}

function AuthenticatedApp() {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation watchdog: log unexpected automatic redirects from '/' to '/meldingen'
  const prevLocationRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevLocationRef.current;
    const cur = location.pathname;
    if (prev === '/' && cur === '/meldingen') {
      try {
        console.warn('[NavigationWatch] Detected navigation from / -> /meldingen', { prev, cur, time: new Date().toISOString(), stack: (new Error()).stack });
        try { logBootEvent('auto-redirect', '/ -> /meldingen'); } catch (_) { void 0; }
      } catch (e) { void 0; }
    }
    prevLocationRef.current = cur;
  }, [location.pathname]);

  const {
    approvalResolved,
    approved,
    authLoading,
    demoMode,
    isAdmin,
    isGlobalAdmin,
    isSuperAdmin,
    notificationPreferences,
    notificationsEnabled,
    rolesLoaded,
    setDemoMode,
    setNotificationPreferences,
    setNotificationsEnabled,
    setUserBranchId,
    user,
    userProfile,
    userBranchId,
    userRoleCapabilities,
  } = useAppAuthContext();
  
  
  
  // Fallback state voor logo
  const [branchToken, setBranchToken] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });
  const [meldingen, setMeldingen] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({ meldingen: [], performanceRecords: [], robots: [], groups: [], groupsStatus: 'idle', groupsError: null, resolvedBranchId: null, sourceRecords: 0, manualReview: [], status: 'idle' });
  const [analyticsState, setAnalyticsState] = useState({ status: 'idle', error: null });
  const [meldingenBadgeCount, setMeldingenBadgeCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [gemeenteFilter, setGemeenteFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [robotSearch, setRobotSearch] = useState("");
  const [weekTechnician, setWeekTechnician] = useState(null);
  const [loadingWeekTechnician, setLoadingWeekTechnician] = useState(true);
  const [, setActiveTab] = useState(() => {
    try {
      return (location && location.pathname && location.pathname.replace(/^\//, '')) || 'meldingen';
    } catch (e) {
      return 'meldingen';
    }
  });
  const [meldingRefreshTrigger, setMeldingRefreshTrigger] = useState(0);
  const [homeOverviewStats, setHomeOverviewStats] = useState({
    activeRobots: null,
    locationsCount: null,
    loaded: false,
  });

  // Notificatie data voor header badge
  const [onderdelen, setOnderdelen] = useState([]);
  const [magazijnVoorraad, setMagazijnVoorraad] = useState([]);
  const [bijzonderheden, setBijzonderheden] = useState([]);
  const [garanties, setGaranties] = useState([]);
  const [androidUpdateInfo, setAndroidUpdateInfo] = useState(null);
  const [changelogUpdateInfo, setChangelogUpdateInfo] = useState(null);
  const [publishedFrontendUpdate, setPublishedFrontendUpdate] = useState(null);
  const [frontendUpdateError, setFrontendUpdateError] = useState('');
  const [nativeAndroidVersion, setNativeAndroidVersion] = useState('');
  const [nativeAndroidVersionCode, setNativeAndroidVersionCode] = useState(0);
  const [autoUpdateInProgress, setAutoUpdateInProgress] = useState(false);
  const [autoUpdateStatus, setAutoUpdateStatus] = useState('');
  const [maintenanceGroundTruthState, setMaintenanceGroundTruthState] = useState({
    open: false,
    melding: null,
    newStatus: '',
    context: null,
    saving: false,
  });
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });
  const autoUpdateAttemptedRef = useRef(new Set());
  const hadOfflineSessionRef = useRef(false);
  const lastActivityWriteRef = useRef(0);

  const { playSuccess, playError, playClick, playNotify, playRouteChange, playWarning, playInfo } = useSound();
  const lastRouteSoundRef = useRef(location.pathname);
  const lastInteractionSoundRef = useRef(0);

  // Week Technician ophalen per vestiging
  // Automatische rotatie gebeurt elke maandag 00:00 via Cloud Scheduler
  useEffect(() => {
    if (!approved) return;
    setLoadingWeekTechnician(true);
    async function fetchWeekTech() {
      try {
        const data = await getCurrentWeekTechnician(userBranchId);
        if (data?.user) {
          setWeekTechnician(data.user);
          return;
        }

        // Auto-initialize for branches that already have technicians but no explicit setting yet.
        const technicians = await getTechnicianList(userBranchId);
        if (technicians.length > 0) {
          const defaultTech = technicians[0];
          setWeekTechnician(defaultTech);
          try {
            await setCurrentWeekTechnician(defaultTech, userBranchId);
          } catch (_) {
            // If write is blocked by permissions, keep UI fallback without failing hard.
          }
        } else {
          setWeekTechnician(null);
        }
      } catch (err) {
        const code = err?.code || '';
        if (code === 'permission-denied') {
          console.info('Week technician unavailable for current permissions/branch context');
          try {
            const technicians = await getTechnicianList(userBranchId);
            setWeekTechnician(technicians[0] || null);
          } catch (_) {
            setWeekTechnician(null);
          }
        } else {
          console.error('Error fetching week technician:', err);
          setWeekTechnician(null);
        }
      } finally {
        setLoadingWeekTechnician(false);
      }
    }
    fetchWeekTech();
  }, [approved, userBranchId]);

  
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    // Keep dark-mode class for backwards compatibility with existing styles
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  useLayoutEffect(() => {
    document.body.setAttribute('data-route-path', location.pathname);
    document.documentElement.setAttribute('data-route-path', location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const getActivityStorageKey = (uid) => `rbh:lastActiveAt:${uid || 'anonymous'}`;

    const writeActivityHeartbeat = async (nowTs) => {
      if (!user?.uid) return;
      if (nowTs - lastActivityWriteRef.current < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastActivityWriteRef.current = nowTs;
      try {
        await setDoc(doc(db, 'users', user.uid), { lastActivityAt: serverTimestamp() }, { merge: true });
      } catch (_) {
        // Non-blocking: heartbeat write should not interrupt app usage.
      }
    };

    const refreshSession = async (forceRefresh = false) => {
      const nowTs = Date.now();
      try {
        localStorage.setItem(getActivityStorageKey(user?.uid), String(nowTs));
        localStorage.setItem('rbh:lastActiveAt', String(nowTs));
      } catch (_) {
        void 0;
      }

      await writeActivityHeartbeat(nowTs);

      const currentUser = auth.currentUser;
      if (!currentUser) return;

      try {
        await currentUser.getIdToken(forceRefresh);
      } catch (error) {
        console.warn('Session refresh after resume failed:', error?.message || error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession(true);
      }
    };

    const handleUserActivity = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSession(false);
    };

    const enforceIdleSessionTimeout = async () => {
      if (!user?.uid) return;
      const key = getActivityStorageKey(user.uid);
      let lastActiveAt = 0;
      try {
        lastActiveAt = Number.parseInt(localStorage.getItem(key) || localStorage.getItem('rbh:lastActiveAt') || '0', 10);
      } catch (_) {
        lastActiveAt = 0;
      }

      if (!Number.isFinite(lastActiveAt) || lastActiveAt <= 0) {
        await refreshSession(false);
        return;
      }

      const idleForMs = Date.now() - lastActiveAt;
      if (idleForMs <= SESSION_IDLE_LIMIT_MS) return;

      try {
        await auth.signOut();
      } catch (_) {
        void 0;
      }
      showAppToast('Sessie verlopen na 10 uur inactiviteit. Log opnieuw in.', 'warning', {
        playSuccess,
        playError,
        playWarning,
        playInfo,
        playNotify,
      });
      // IMPORTANT: Do not navigate here — auth changes should be handled by
      // the auth layer and route guards. Removing imperative navigation
      // prevents unexpected redirects during background session tasks.
      console.trace('AUTO NAVIGATION REMOVED: session timeout signOut — no navigate() executed');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pointerdown', handleUserActivity, { passive: true });
    window.addEventListener('keydown', handleUserActivity, { passive: true });
    window.addEventListener('touchstart', handleUserActivity, { passive: true });

    let appStateListener = null;
    Promise.resolve(
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) refreshSession(true);
      })
    )
      .then((listener) => {
        appStateListener = listener;
      })
      .catch(() => {
        appStateListener = null;
      });

    const idleTimer = window.setInterval(() => {
      void enforceIdleSessionTimeout();
    }, 60 * 1000);

    void refreshSession(false);
    void enforceIdleSessionTimeout();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pointerdown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      window.clearInterval(idleTimer);
      try {
        appStateListener?.remove?.();
      } catch (_) {
        void 0;
      }
    };
  }, [navigate, playError, playInfo, playNotify, playSuccess, playWarning, user?.uid]);

  const userWithRoleCapabilities = useMemo(() => {
    if (!user) return null;
    return {
      ...user,
      ...userRoleCapabilities,
      branchId: userBranchId || userProfile?.branchId || null,
      branchesAllowed: Array.isArray(userProfile?.branchesAllowed) ? userProfile.branchesAllowed : [],
      branchIds: Array.isArray(userProfile?.branchIds) ? userProfile.branchIds : [],
      isGlobalAdmin: Boolean(isGlobalAdmin || userProfile?.isGlobalAdmin === true || userProfile?.isSuperAdmin === true),
      isSuperAdmin: Boolean(userProfile?.isSuperAdmin === true || userProfile?.isGlobalAdmin === true),
    };
  }, [isGlobalAdmin, user, userBranchId, userProfile, userRoleCapabilities]);

  const demoModeEnabled = Boolean(demoMode.enabled && userRoleCapabilities.canManageAssignedVestiging);
  const demoShowroomEnabled = Boolean(demoModeEnabled && demoMode.showroom);
  const effectiveIsSuperAdmin = Boolean(isSuperAdmin || demoModeEnabled);
  const effectiveUserBranchId = demoModeEnabled ? null : userBranchId;
  const headerNavigationContext = useMemo(() => ({
    adminHomePath: userRoleCapabilities.technician && !isAdmin ? '/administratie/check-lists' : '/administratie',
    canAccessAdminNav: Boolean(isAdmin || userRoleCapabilities.technician),
    canAccessBijzonderheden: Boolean(userRoleCapabilities.technician || isAdmin),
    canAccessInfraStatus: Boolean(userRoleCapabilities.developer || userRoleCapabilities.canManageAllVestigingen || isSuperAdmin),
    canAccessReports: Boolean(isAdmin),
    demoModeEnabled,
    demoShowroomEnabled,
  }), [demoModeEnabled, demoShowroomEnabled, isAdmin, isSuperAdmin, userRoleCapabilities]);

  const changelogEntries = useMemo(() => {
    const rawEntries = Array.isArray(changelogManifest?.versions) ? changelogManifest.versions : [];
    return rawEntries
      .filter((entry) => entry && typeof entry.version === 'string')
      .slice()
      .sort((a, b) => compareReleaseVersions(b.version, a.version));
  }, []);

  const latestChangelogVersion = useMemo(() => {
    if (typeof changelogManifest?.latest === 'string' && changelogManifest.latest.trim()) {
      return changelogManifest.latest.trim();
    }
    return changelogEntries[0]?.version || '';
  }, [changelogEntries]);

  useEffect(() => {
    if (!approved || !user?.uid || !latestChangelogVersion) {
      setPublishedFrontendUpdate(null);
      return undefined;
    }
    let active = true;
    let lastCheckAt = 0;
    const checkPublishedVersion = async () => {
      if (Date.now() - lastCheckAt < 60_000) return;
      lastCheckAt = Date.now();
      try {
        const response = await fetch(`/documentatie/changelog.json?release-check=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const manifest = await response.json();
        const version = typeof manifest?.latest === 'string' ? manifest.latest.trim() : '';
        if (!active || !version) return;
        setPublishedFrontendUpdate(hasNewerPublishedRelease(latestChangelogVersion, version)
          ? { version, entries: Array.isArray(manifest.versions) ? manifest.versions : [] }
          : null);
      } catch {
        // A failed static manifest check must never be presented as an available update.
        if (active) setPublishedFrontendUpdate(null);
      }
    };
    void checkPublishedVersion();
    window.addEventListener('focus', checkPublishedVersion);
    return () => {
      active = false;
      window.removeEventListener('focus', checkPublishedVersion);
    };
  }, [approved, user?.uid, latestChangelogVersion]);

  useEffect(() => {
    if (!user?.uid || !latestChangelogVersion) return;
    const key = `rbh:pending-frontend-update:${user.uid}`;
    let expectedVersion = '';
    try { expectedVersion = window.sessionStorage.getItem(key) || ''; } catch { return; }
    if (!expectedVersion) return;
    try { window.sessionStorage.removeItem(key); } catch { /* session storage is optional */ }
    if (compareReleaseVersions(latestChangelogVersion, expectedVersion) >= 0) {
      void (async () => {
        try {
          await setDoc(doc(db, 'users', user.uid), {
            changelogLastSeenVersion: expectedVersion,
            changelogLastSeenAt: serverTimestamp(),
          }, { merge: true });
          applyUpdatePrompt({ userId: user.uid, version: expectedVersion, storage: window.localStorage });
        } catch (error) {
          console.warn('Confirmed frontend update read-state could not be synchronized:', error?.message || error);
        }
      })();
      return;
    }
    setFrontendUpdateError(`RBH ${expectedVersion} is na het vernieuwen niet bevestigd. Probeer het later opnieuw.`);
  }, [user?.uid, latestChangelogVersion]);

  useEffect(() => {
    if (!approved || !user?.uid || !latestChangelogVersion) {
      setChangelogUpdateInfo(null);
      return;
    }

    const availableVersion = publishedFrontendUpdate?.version || latestChangelogVersion;
    const decision = getChangelogUpdateDecision({
      userId: user.uid,
      latestVersion: availableVersion,
      acknowledgedVersion: userProfile?.changelogLastSeenVersion || '',
      storage: window.localStorage,
    });
    if (decision.state !== UPDATE_STATES.UPDATE_AVAILABLE) {
      setChangelogUpdateInfo(null);
      return;
    }
    const availableEntries = publishedFrontendUpdate?.entries || changelogEntries;
    const unseenEntries = getUnseenChangelogEntries(availableEntries, decision.lastSeenVersion);

    setChangelogUpdateInfo({
      toVersion: availableVersion,
      isNewerFrontendAvailable: Boolean(publishedFrontendUpdate),
      updateError: frontendUpdateError,
      totalUnseen: unseenEntries.length,
      entries: unseenEntries.slice(0, CHANGELOG_MAX_PROMPT_ITEMS),
    });
  }, [approved, user?.uid, userProfile?.changelogLastSeenVersion, changelogEntries, latestChangelogVersion, publishedFrontendUpdate, frontendUpdateError]);

  const markChangelogUpdatesLater = () => {
    dismissUpdatePrompt({ userId: user?.uid, version: changelogUpdateInfo?.toVersion || latestChangelogVersion, storage: window.localStorage });
    setChangelogUpdateInfo(null);
  };

  const applyChangelogUpdate = async () => {
    if (!user?.uid || !latestChangelogVersion) return;
    if (publishedFrontendUpdate) {
      try {
        window.sessionStorage.setItem(`rbh:pending-frontend-update:${user.uid}`, publishedFrontendUpdate.version);
        const url = new URL(window.location.href);
        url.searchParams.set('rbh-refresh', String(Date.now()));
        window.location.assign(url.toString());
      } catch {
        setFrontendUpdateError('Vernieuwen is niet gestart. Controleer of sessieopslag is toegestaan en probeer opnieuw.');
      }
      return;
    }
    try {
      await setDoc(doc(db, 'users', user.uid), {
        changelogLastSeenVersion: latestChangelogVersion,
        changelogLastSeenAt: serverTimestamp(),
      }, { merge: true });
      applyUpdatePrompt({ userId: user.uid, version: latestChangelogVersion, storage: window.localStorage });
      setChangelogUpdateInfo(null);
    } catch (error) {
      console.warn('Changelog read-state could not be synchronized:', error?.message || error);
    }
  };

  // AUTO-SYNC: Automatically reload data when user role or vestiging changes
  useEffect(() => {
    // Defer data loads briefly so the initial UI can render quickly.
    const DELAY_MS = Number(process.env.REACT_APP_MELDINGEN_LOAD_DELAY_MS || 700);
    const routeDataPlan = getAppRouteDataLoadPlan(location.pathname, {
      isAdmin: Boolean(isAdmin || effectiveIsSuperAdmin),
    });
    const t = setTimeout(() => {
      // The public overview endpoint is only needed by the homepage.
      if (routeDataPlan.homeOverview) void loadHomeOverviewStats();

      if (user && approved) {
        if (!routeDataPlan.skipGlobalMeldingen && location.pathname !== '/analytics/rpd-manager' && location.pathname !== '/analytics/dossier') void loadMeldingen();
        // Analytics screens load their own sources; don't read them on unrelated routes.
        if (routeDataPlan.analytics) void loadAnalytics();
        if (routeDataPlan.partsInventory) {
          void loadOnderdelen();
          void loadMagazijnVoorraad();
        }
        // These datasets are consumed by ProfileTab, the only route receiving them.
        if (routeDataPlan.profileDetails) {
          void loadBijzonderheden();
          void loadGaranties();
        }
      }
    }, DELAY_MS);

    return () => clearTimeout(t);
  }, [location.pathname, user, approved, userBranchId, isAdmin, isSuperAdmin, effectiveIsSuperAdmin, branchToken, demoModeEnabled]);

  const updateDemoModeSettings = async ({ enabled, showroom }) => {
    if (!user) return;
    const canUseDemoForUser = Boolean(userRoleCapabilities.canManageAssignedVestiging);
    if (!canUseDemoForUser) {
      showToast('❌ Demonstratiemodus is alleen beschikbaar voor uitvoerder of hoger.', 'error');
      return;
    }

    const next = {
      enabled: Boolean(enabled),
      showroom: Boolean(showroom),
      updatedAt: serverTimestamp(),
      enabledAt: enabled ? serverTimestamp() : null,
    };

    await setDoc(doc(db, 'users', user.uid), {
      demoMode: next,
    }, { merge: true });

    setDemoMode({ enabled: next.enabled, showroom: next.showroom });
    if (next.enabled) {
      showToast('Demonstratiemodus ingeschakeld', 'success');
    } else {
      showToast('Demonstratiemodus uitgeschakeld', 'info');
    }
  };

  const handleIncomingNotification = (notification = {}, data = {}) => {
    const title = notification?.title || 'Nieuwe melding';
    const body = notification?.body || 'Je hebt een nieuwe update.';
    const payloadData = data || notification?.data || {};

    showToast(body, 'info');
    void sendBrowserNotification(title, body, '/krinkels-icon-192.png', {
      tag: payloadData.type || 'incoming-melding'
    });

    switch (payloadData.type) {
      case 'new_melding':
        loadMeldingen();
        loadAnalytics();
        playSuccess && playSuccess();
        break;
      case 'new_bijzonderheid':
        loadBijzonderheden();
        playInfo && playInfo();
        break;
      case 'new_garantie':
        loadGaranties();
        playInfo && playInfo();
        break;
      case 'new_onderdeel':
        loadOnderdelen();
        loadMagazijnVoorraad();
        playInfo && playInfo();
        break;
      case 'new_robot':
        playInfo && playInfo();
        break;
      default:
        playInfo && playInfo();
        break;
    }
  };

  useEffect(() => {
    const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    if (!isNativeAndroid) return;

    let mounted = true;

    const resolveNativeVersion = async () => {
      try {
        const info = await CapacitorApp.getInfo();
        if (!mounted) return;
        const parsedBuild = Number.parseInt(String(info?.build || ''), 10);

        if (info?.version) {
          setNativeAndroidVersion(String(info.version));
        }
        if (Number.isFinite(parsedBuild)) {
          setNativeAndroidVersionCode(parsedBuild);
        }
      } catch (_) {
        // keep fallback constants
      }
    };

    resolveNativeVersion();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    if (!isNativeAndroid) return;

    const manifestUrl = getAndroidUpdateManifestUrl();
    if (!manifestUrl) return;

    let cancelled = false;

    const checkAndroidUpdate = async () => {
      try {
        const requestUrl = `${manifestUrl}${manifestUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
        const response = await fetch(requestUrl, { cache: 'no-store' });
        if (!response.ok) return;

        const payload = await response.json();
        if (cancelled || !payload || !payload.apkUrl) return;

        const effectiveCurrentVersion = nativeAndroidVersion || CURRENT_ANDROID_VERSION;
        const effectiveCurrentVersionCode = nativeAndroidVersionCode || CURRENT_ANDROID_VERSION_CODE;

        const hasUpdate = isNewerAndroidBuild({
          currentVersion: effectiveCurrentVersion,
          currentVersionCode: effectiveCurrentVersionCode,
          remoteVersion: payload.version,
          remoteVersionCode: payload.versionCode
        });

        if (!hasUpdate) return;

        const dismissedKey = `androidUpdateDismissed:${payload.versionCode || payload.version || 'latest'}`;
        const dismissed = window.localStorage.getItem(dismissedKey) === '1';
        if (!dismissed) {
          setAndroidUpdateInfo({ ...payload, dismissedKey });
        }
      } catch (_) {
        // silent fail, update checks are optional
      } finally {
        void cancelled;
      }
    };

    checkAndroidUpdate();
    return () => {
      cancelled = true;
    };
  }, [nativeAndroidVersion, nativeAndroidVersionCode]);

  const installAndroidUpdate = async (updatePayload) => {
    await installAndroidUpdateFlow({
      updatePayload,
      currentAndroidVersion: CURRENT_ANDROID_VERSION,
      setAutoUpdateInProgress,
      setAutoUpdateStatus,
    });
  };

  useEffect(() => {
    const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    if (!isNativeAndroid || !androidUpdateInfo?.apkUrl) return;

    const updateId = String(androidUpdateInfo.versionCode || androidUpdateInfo.version || 'latest');
    if (autoUpdateAttemptedRef.current.has(updateId)) return;

    autoUpdateAttemptedRef.current.add(updateId);
    void installAndroidUpdate(androidUpdateInfo);
  }, [androidUpdateInfo]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      hadOfflineSessionRef.current = true;
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const downloadLatestAndroidApk = async () => {
    await downloadLatestAndroidApkFlow({
      setAndroidUpdateInfo,
      setAutoUpdateStatus,
      onInstall: installAndroidUpdate,
      showToast,
    });
  };

  const fetchAndroidUpdate = async () => {
    await fetchAndroidUpdateFlow({
      nativeAndroidVersion,
      nativeAndroidVersionCode,
      currentAndroidVersion: CURRENT_ANDROID_VERSION,
      currentAndroidVersionCode: CURRENT_ANDROID_VERSION_CODE,
      setAndroidUpdateInfo,
      setAutoUpdateStatus,
      onInstall: installAndroidUpdate,
      showToast,
    });
  };

  // Handle hardware back button - navigate to previous page instead of closing app
  useEffect(() => {
    const navigationHistory = [];

    // Track navigation history
    const handleLocationChange = () => {
      const currentPath = location.pathname;
      if (navigationHistory[navigationHistory.length - 1] !== currentPath) {
        navigationHistory.push(currentPath);
      }
    };

    handleLocationChange();

    // Check if running on Android and set up back button handler
    const setupBackButton = async () => {
      const isAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
      if (!isAndroid) return;

      try {
        const { App: CapApp } = await import('@capacitor/app');
            CapApp.addListener('backButton', ({ canGoBack }) => {
          // If there's history to go back to, navigate back
          if (navigationHistory.length > 1) {
            navigationHistory.pop(); // Remove current location
            const previousPath = navigationHistory[navigationHistory.length - 1];
                // Avoid automatic navigation to /meldingen — prefer root fallback
                if (!previousPath) {
                  console.trace('AUTO REDIRECT TRIGGERED: backButton fallback to /');
                }
                navigate(previousPath || '/');
          } else if (canGoBack) {
            // If no internal history, let the app handle it (might close)
            // But we want to stay in app, so navigate to home instead
                console.trace('AUTO REDIRECT TRIGGERED: backButton no-history fallback to /');
                navigate('/');
          }
        });
      } catch (e) {
        // Capacitor App not available, fall back to browser back
        window.addEventListener('popstate', () => {
          if (navigationHistory.length > 1) {
            navigationHistory.pop();
          }
        });
      }
    };

    setupBackButton();
  }, [location.pathname, navigate]);

  const toggleNotifications = async () => {
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState);

    try {
      await toggleNotificationsSetting({
        db,
        userId: user.uid,
        notificationsEnabled: newState,
        notificationPreferences,
        normalizeNotificationPreferences,
      });
      showToast(`🔔 Notificaties ${newState ? 'ingeschakeld' : 'uitgeschakeld'}`, 'success');
      showToast(`Notificaties ${newState ? 'ingeschakeld' : 'uitgeschakeld'}`, 'success');
    } catch (error) {
      console.error('Error updating notifications:', error);
    }
  };

  const toggleNotificationPreference = async (category) => {
    const currentValue = notificationPreferences[category] !== false;
    const nextPreferences = {
      ...notificationPreferences,
      [category]: !currentValue,
    };

    setNotificationPreferences(nextPreferences);

    try {
      await toggleNotificationPreferenceSetting({
        db,
        userId: user.uid,
        nextPreferences,
      });
      showToast(`🔔 ${category} notificaties ${!currentValue ? 'ingeschakeld' : 'uitgeschakeld'}`, 'success');
    } catch (error) {
      console.error('Error updating notification preference:', error);
    }
  };

  const loadMeldingen = async () => {
    invalidateAdminMeldingenCache();
    const { meldingenData } = await loadMeldingenData({
      db,
      userBranchId: effectiveUserBranchId,
      isSuperAdmin: effectiveIsSuperAdmin,
      logger,
      user,
      resolveVestigingIdWithData,
      canonicalizeVestigingId,
      getVestigingIdCandidates,
    });
    setMeldingen(meldingenData);
    setMeldingenBadgeCount(countUnreadHumanMeldingen(meldingenData, effectiveUserBranchId));
  };

  const syncAdminPanelMeldingen = useCallback((loadedMeldingen = []) => {
    const rows = Array.isArray(loadedMeldingen) ? loadedMeldingen : [];
    setMeldingen(rows);
    setMeldingenBadgeCount(countUnreadHumanMeldingen(rows, effectiveUserBranchId));
  }, [effectiveUserBranchId]);

  const loadAnalytics = async () => {
    if (!effectiveUserBranchId) {
      setAnalyticsState({ status: 'idle', error: null });
      return;
    }
    setAnalyticsState({ status: 'loading', error: null });
    try {
      const data = location.pathname === '/analytics/rpd-manager' || location.pathname === '/analytics/dossier'
        ? await loadAnalyticsSelectorData({ db, branchId: effectiveUserBranchId, resolveVestigingIdWithData })
        : await loadAnalyticsData({ db, branchId: effectiveUserBranchId, resolveVestigingIdWithData });
      setAnalyticsData({
        ...data,
        meldingen: data.meldingen || [],
        performanceRecords: data.performanceRecords || [],
        manualReview: data.manualReview || [],
      });
      setAnalyticsState({ status: 'ready', error: null });
    } catch (error) {
      logger.error('ANALYTICS_LOAD', 'Branch-only Analytics load failed', error);
      setAnalyticsData({ meldingen: [], performanceRecords: [], robots: [], groups: [], groupsStatus: 'error', groupsError: error?.message || null, sourceRecords: 0, manualReview: [], status: 'error' });
      setAnalyticsState({ status: 'error', error: error?.message || 'Analyticsdata kon niet worden geladen.' });
      setMeldingenBadgeCount(0);
    }
  };

  const loadSelectedRobotPerformance = useCallback((robot, pageCursors = {}, range = {}) => loadRobotPerformanceData({
    db,
    branchId: effectiveUserBranchId,
    robot,
    pageCursors,
    periodFrom: range.periodFrom,
    periodTo: range.periodTo,
    resolveVestigingIdWithData,
  }), [effectiveUserBranchId, resolveVestigingIdWithData]);

  const loadHomeOverviewStats = async () => {
    try {
      try {
        const publicOverviewUrl = process.env.REACT_APP_PUBLIC_OVERVIEW_URL
          || 'https://us-central1-myrobot-krinkels.cloudfunctions.net/getPublicOverviewStatsV2';
        const response = await fetch(publicOverviewUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const payload = await response.json();
          const serverStats = payload?.stats || {};
          const stats = {
            activeRobots: Number(serverStats.activeRobots || 0),
            totalRobots: Number(serverStats.activeRobots ?? serverStats.totalRobots ?? 0),
            meldingenCount: Number(serverStats.meldingenCount || 0),
            locationsCount: Number(serverStats.locationsCount || 0),
            distanceKm: Number.isFinite(Number(serverStats.distanceKm)) ? Number(serverStats.distanceKm) : null,
            coveredAreaHa: Number.isFinite(Number(serverStats.coveredAreaHa)) ? Number(serverStats.coveredAreaHa) : null,
            footballFields: Number.isFinite(Number(serverStats.footballFields)) ? Number(serverStats.footballFields) : null,
            areaIsEstimated: serverStats.areaIsEstimated === true,
            loaded: true,
          };
          setHomeOverviewStats(stats);
          if (typeof window !== 'undefined') {
            window.__RBH_STATS__ = {
              activeRobots: stats.activeRobots,
              locationsCount: stats.locationsCount,
            };
          }
          return;
        }
      } catch (_) {
        // Public pages must never fall back to private Firestore reads.
        setHomeOverviewStats((prev) => ({ ...prev, loaded: false }));
        return;
      }
      setHomeOverviewStats((prev) => ({ ...prev, loaded: false }));
    } catch (error) {
      logger.warn('HOME_OVERVIEW', 'Public overview stats request failed', error?.message || error);
      setHomeOverviewStats((prev) => ({ ...prev, loaded: false }));
    }
  };

  const filteredMeldingen = meldingen.filter(m => {
    const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const normalizedGemeente = normalizeFilterValue(m.gemeente || m.branchName || m.vestiging || '');
    const selectedGemeente = normalizeFilterValue(gemeenteFilter);

    if (selectedGemeente && normalizedGemeente !== selectedGemeente) return false;
    if (robotSearch) {
      const needle = normalizeFilterValue(robotSearch);
      const robotHaystack = [
        m.naam,
        m.robotName,
        m.robotNaam,
        m.robot,
        m.selectedRobot?.label,
        m.selectedRobot?.value,
        m.selectedRobot?.name,
      ]
        .filter(Boolean)
        .map((value) => normalizeFilterValue(value))
        .join(' ');
      if (!robotHaystack.includes(needle)) return false;
    }
    if (dateFrom || dateTo) {
      const meldingDate = m.timestamp?.toDate ? m.timestamp.toDate() : new Date(m.timestamp);
      if (dateFrom && meldingDate < new Date(dateFrom)) return false;
      if (dateTo) {
        const endOfDay = new Date(`${dateTo}T23:59:59.999`);
        if (meldingDate > endOfDay) return false;
      }
    }
    return true;
  });

  const addMelding = async (meldingData) => {
    if (demoModeEnabled) {
      showToast('🎬 Demonstratiemodus: opslaan van meldingen is uitgeschakeld.', 'warning');
      return;
    }
    const createdRef = await createMeldingEntry({
      db,
      auth,
      user,
      userBranchId: effectiveUserBranchId,
      meldingData,
      logger,
      resolveVestigingDocId,
      canonicalizeVestigingId,
      getVestigingIdCandidates,
      getCanonicalRobotName,
      logPartsUsage,
      logAuditEvent,
      AuditActions,
      onReload: loadMeldingen,
    });
    recordRobotRoundEvent({
      enabled: userProfile?.betaWorkdayAssistant === true,
      uid: user?.uid,
      event: {
        type: 'MELDING',
        serial: meldingData?.selectedRobot?.robot?.serial || meldingData?.selectedRobot?.robot?.robotSerial || meldingData?.robotSerial || meldingData?.selectedRobot?.value || null,
        meldingId: createdRef?.id || null,
      },
    });
  };

  const deleteMelding = async (meldingIdOrObject, options = {}) => {
    if (demoModeEnabled) {
      showToast('🎬 Demonstratiemodus: verwijderen is uitgeschakeld.', 'warning');
      return;
    }
    const { skipConfirm = false } = options || {};
    try {
      await deleteMeldingEntry({
        db,
        auth,
        meldingIdOrObject,
        userBranchId: effectiveUserBranchId,
        logAuditEvent,
        AuditActions,
        onReload: loadMeldingen,
        onRefresh: () => setMeldingRefreshTrigger((prev) => prev + 1),
        skipConfirm,
      });
      showToast('✅ Melding verwijderd', 'success');
    } catch (error) {
      console.error('Error deleting melding:', error);
      showToast('❌ Verwijderen mislukt: ' + error.message, 'error');
    }
  };

  // Notificatie functies voor header badge
  const loadOnderdelen = async () => {
    const data = await loadOnderdelenData({
      db,
      userBranchId: effectiveUserBranchId || userBranchId,
      resolveVestigingIdWithData,
    });
    setOnderdelen(data);
  };

  const loadMagazijnVoorraad = async () => {
    const voorraad = await loadMagazijnVoorraadData(effectiveUserBranchId || userBranchId);
    setMagazijnVoorraad(voorraad);
  };

  const loadBijzonderheden = async () => {
    const data = await loadBijzonderhedenData({
      db,
      userBranchId: effectiveUserBranchId || userBranchId,
      resolveVestigingIdWithData,
    });
    setBijzonderheden(data);
  };

  const loadGaranties = async () => {
    const data = await loadGarantiesData({
      db,
      userBranchId: effectiveUserBranchId || userBranchId,
      resolveVestigingIdWithData,
    });
    setGaranties(data);
  };

  const showToast = (message, type = 'info') => {
    showAppToast(message, type, {
      playSuccess,
      playError,
      playWarning,
      playInfo,
      playNotify,
    });
  };

  const {
    dismissPermissionsPrompt,
    gelezenNotificaties,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    notificationCount,
    notifications,
    permissionStatus,
    requestAppPermissions,
    requestingPermissions,
    refreshPermissionStatus,
    sendTestNotification,
    setGelezenNotificaties,
    setShowNotificationPopup,
    showNotificationPopup,
    showPermissionPrompt,
  } = useAppNotifications({
    approved,
    bijzonderheden,
    garanties,
    magazijnVoorraad,
    notificationPreferences,
    notificationsEnabled,
    onIncomingNotification: handleIncomingNotification,
    onderdelen,
    showToast,
    user,
  });

  useEffect(() => {
    if (!user || !approved || !isOnline || !hadOfflineSessionRef.current) return;

    let cancelled = false;

    const flushOfflineWrites = async () => {
      try {
        await waitForPendingWrites(db);
        if (!cancelled) {
          showToast('🔄 Offline data is gesynchroniseerd.', 'success');
          hadOfflineSessionRef.current = false;
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Synchronisatie na reconnect mislukt:', error);
        }
      }
    };

    flushOfflineWrites();

    return () => {
      cancelled = true;
    };
  }, [isOnline, user, approved]);

  useEffect(() => {
    if (lastRouteSoundRef.current === location.pathname) return;
    lastRouteSoundRef.current = location.pathname;
    try {
      playRouteChange && playRouteChange();
    } catch (_) {
      void 0;
    }
  }, [location.pathname, playRouteChange]);

  useEffect(() => {
    const isInteractiveElement = (el) => {
      if (!el || typeof el.closest !== 'function') return false;
      return !!el.closest(
        'button, a, [role="button"], input[type="checkbox"], input[type="radio"], select, summary, .nav-item, .control-btn, .krinkels-button'
      );
    };

    const onPointerDown = (event) => {
      const target = event.target;
      if (!isInteractiveElement(target)) return;
      const now = Date.now();
      if (now - lastInteractionSoundRef.current < 70) return;
      lastInteractionSoundRef.current = now;
      try {
        playClick && playClick();
      } catch (_) {
        void 0;
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [playClick]);

  const handleLogout = async () => {
    try {
      setShowNotificationPopup(false);
      await auth.signOut();
      navigate('/', { replace: true });
    } catch (e) {
      console.error('Logout failed:', e);
      showToast('❌ Uitloggen mislukt: ' + (e?.message || String(e)), 'error');
    } finally {
      setActiveTab("meldingen");
    }
  };

  const navigateAndLog = (path, options = undefined) => {
    try {
      logRouteDebug('nav click ->', path, 'from', location.pathname);
    } catch (e) {
      // Log unexpected errors to help diagnostics in production builds
      try { console.warn('[RouteDebug] navigateAndLog error', e); } catch (_) { void 0; }
    }
    navigate(path, options || undefined);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    // FIXED: Don't toggle directly - let useEffect handle it based on state
  };

  const { voiceSystem, startVoiceRecognition, stopVoiceRecognition } = useAppVoiceAssistant({
    user,
    isAdmin,
    isSuperAdmin,
    locationPath: location.pathname,
    navigate,
    showToast,
    meldingen,
    filteredMeldingen,
    onderdelen,
    magazijnVoorraad,
    userBranchId,
    weekTechnician,
    getDisplayName,
    nativeAndroidVersion,
    currentAndroidVersion: CURRENT_ANDROID_VERSION,
    isOnline,
    androidUpdateInfo,
    fetchAndroidUpdate,
    downloadLatestAndroidApk,
    notificationCount,
    notificationsEnabled,
    toggleNotifications,
    darkMode,
    toggleDarkMode,
  });

  // Migration/admin helper functions were removed because the UI no longer exposes
  // the super-admin migration controls. Keep the implementation in server-side
  // scripts under `functions/` for manual execution to avoid accidental runs.

  // UPDATE MELDING STATUS
  const applyStatusUpdate = async (meldingId, newStatus) => {
    await updateMeldingStatusEntry({
      db,
      auth,
      user,
      meldingId,
      newStatus,
      userBranchId: effectiveUserBranchId,
      getDisplayName,
      logAuditEvent,
      AuditActions,
      createChangesObject,
      onReload: loadMeldingen,
    });
  };

  const updateMeldingStatus = async (meldingId, newStatus) => {
    if (demoModeEnabled) {
      showToast('🎬 Demonstratiemodus: statuswijzigingen zijn uitgeschakeld.', 'warning');
      return;
    }

    const meldingObject = typeof meldingId === 'object' && meldingId ? meldingId : null;

    if (meldingObject && shouldPromptMaintenanceGroundTruth({ melding: meldingObject, newStatus })) {
      try {
        const context = await prepareMaintenanceGroundTruthContext({
          branchId: effectiveUserBranchId,
          melding: meldingObject,
        });

        if (context) {
          setMaintenanceGroundTruthState({
            open: true,
            melding: meldingObject,
            newStatus,
            context,
            saving: false,
          });
          return;
        }
      } catch (error) {
        console.warn('Onderhouds ground-truth voorbereiding mislukt:', error);
      }
    }

    try {
      await applyStatusUpdate(meldingId, newStatus);
      showToast('✅ Status bijgewerkt', 'success');
      showToast('Status bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('❌ Status update mislukt: ' + error.message, 'error');
      showToast('Status update mislukt: ' + error.message, 'error');
      showToast('Status bijgewerkt', 'success');
      showToast('Status update mislukt: ' + error.message, 'error');
    }
  };

  const closeMaintenanceGroundTruthModal = () => {
    setMaintenanceGroundTruthState({
      open: false,
      melding: null,
      newStatus: '',
      context: null,
      saving: false,
    });
  };

  const skipMaintenanceGroundTruth = async () => {
    if (!maintenanceGroundTruthState.melding) return closeMaintenanceGroundTruthModal();
    setMaintenanceGroundTruthState((prev) => ({ ...prev, saving: true }));
    try {
      await applyStatusUpdate(maintenanceGroundTruthState.melding, maintenanceGroundTruthState.newStatus);
      showToast('✅ Status bijgewerkt', 'success');
      closeMaintenanceGroundTruthModal();
    } catch (error) {
      console.error('Error updating status after skip:', error);
      showToast('❌ Status update mislukt: ' + error.message, 'error');
      setMaintenanceGroundTruthState((prev) => ({ ...prev, saving: false }));
    }
  };

  const saveMaintenanceGroundTruthAndClose = async ({
    inspectionOutcome,
    missingBladeCount,
    damagedBladeCount,
    missingByHead,
    replacedByHead,
    confirmedHeadIds,
    notes,
  }) => {
    if (!maintenanceGroundTruthState.melding || !maintenanceGroundTruthState.context) {
      closeMaintenanceGroundTruthModal();
      return;
    }

    setMaintenanceGroundTruthState((prev) => ({ ...prev, saving: true }));
    try {
      const openCases = await getDocs(collection(db, ...branchCollectionPath(maintenanceGroundTruthState.context.branchId, 'maintenanceValidationCases')));
      const relatedCase = openCases.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .find((item) => item.status === 'OPEN'
          && item.preInspectionSnapshot?.robotSerial === maintenanceGroundTruthState.context.robotSerial
          && item.preInspectionSnapshot?.cycleId
          && item.preInspectionSnapshot.cycleId === maintenanceGroundTruthState.context.cycleId);
      await applyStatusUpdate(maintenanceGroundTruthState.melding, maintenanceGroundTruthState.newStatus);
      await saveMaintenanceGroundTruth({
        branchId: maintenanceGroundTruthState.context.branchId,
        robotSerial: maintenanceGroundTruthState.context.robotSerial,
        robotLabel: maintenanceGroundTruthState.context.robotLabel,
        cycleId: maintenanceGroundTruthState.context.cycleId,
        parcelId: maintenanceGroundTruthState.context.parcelId,
        sourceRobotId: maintenanceGroundTruthState.context.sourceRobotId,
        canonicalRobotId: maintenanceGroundTruthState.context.canonicalRobotId,
        relatedNotificationId: maintenanceGroundTruthState.melding?.id,
        relatedValidationCaseId: relatedCase?.id || null,
        inspectionOutcome,
        missingBladeCount,
        damagedBladeCount,
        missingByHead,
        replacedByHead,
        confirmedHeadIds,
        interventionId: maintenanceGroundTruthState.melding?.id || null,
        visited: true,
        answeredAt: new Date().toISOString(),
        notes,
        predictedByHead: maintenanceGroundTruthState.context.predictedByHead,
        user,
        closedAtMs: Date.now(),
      });
      if (relatedCase) {
        await updateDoc(doc(db, ...branchDocumentPath(maintenanceGroundTruthState.context.branchId, 'maintenanceValidationCases', relatedCase.id)), {
          status: 'COMPLETED',
          completedAt: serverTimestamp(),
          physicalResult: { inspectionOutcome, missingBladeCount, damagedBladeCount, missingByHead, replacedByHead, confirmedHeadIds, notes },
        });
      }
      showToast('✅ Status bijgewerkt en onderhoudsdata opgeslagen', 'success');
        showToast('Status bijgewerkt en onderhoudsdata opgeslagen', 'success');
      closeMaintenanceGroundTruthModal();
    } catch (error) {
      console.error('Onderhouds ground-truth opslaan mislukt:', error);
      showToast('❌ Opslaan mislukt: ' + (error?.message || String(error)), 'error');
        showToast('Opslaan mislukt: ' + (error?.message || String(error)), 'error');
      setMaintenanceGroundTruthState((prev) => ({ ...prev, saving: false }));
    }
  };

  // EXPORT FILTERED DATA
  const exportFilteredData = () => {
    try {
      exportMeldingenCsv(filteredMeldingen);
      showToast("✅ Export succesvol", "success");
      showToast("Export succesvol", "success");
    } catch (error) {
      console.error("Export error:", error);
      showToast("❌ Export mislukt", "error");
      showToast("Export mislukt", "error");
    }
  };

  // CLEAR ALL FILTERS
  const clearAllFilters = () => {
    setGemeenteFilter("");
    setDateFrom("");
    setDateTo("");
    setRobotSearch("");
    showToast("🗑️ Filters gewist", "info");
    showToast("Filters gewist", "info");
  };

  const routeContentKey = `${location.key || 'direct'}:${location.pathname}${location.search || ''}${location.hash || ''}`;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[route-state]', {
        reactPath: location.pathname,
        browserPath: typeof window !== 'undefined' ? window.location.pathname : location.pathname,
        locationKey: location.key || 'direct',
        routeContentKey,
      });
    }
  }, [location.hash, location.key, location.pathname, location.search, routeContentKey]);


  // Auth gating: only block while the auth client is still initializing.
  // Do not block routes while `userDoc`/approval is resolving — render with safe defaults.
  if (authLoading) {
    return (
      <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
        <LoadingScreen message="Laden..." />
      </div>
    );
  }

  // Allow homepage to be accessible without authentication
  const isHomePage = location.pathname === '/';

  // Public pages are rendered outside the authenticated app shell.
  // This prevents blank screens on devices where auth has not initialized yet.
  if (!user) {
    if (isHomePage) {
      return (
        <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
          {process.env.REACT_APP_SHOW_DEVELOPMENT_BANNER === 'true' && <DevelopmentBanner />}
          <HomePage meldingen={meldingen} homeOverviewStats={homeOverviewStats} />
        </div>
      );
    }
    return (
      <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
        {process.env.REACT_APP_SHOW_DEVELOPMENT_BANNER === 'true' && <DevelopmentBanner />}
        <Login />
      </div>
    );
  }

  // If approval has been explicitly resolved to false, show the pending screen.
  // If approval is still resolving (null) allow normal routes to render with safe defaults.
  if (user && approved === false && approvalResolved) {
    return (
      <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
        <ApprovalPendingScreen
          darkMode={darkMode}
          publicLogo={publicLogo}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  return (
    <BranchProvider user={user} onBranchChange={setUserBranchId} onBranchVersionChange={setBranchToken}>
      <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <CookieBanner />

      {user && (
        <>
      <AppHeader
        publicLogo={publicLogo}
        belRoboticsLogo={belRoboticsLogo}
        husqvarnaLogo={husqvarnaLogo}
        kressLogo={kressLogo}
        locationPath={location.pathname}
        navigateAndLog={navigateAndLog}
        meldingenBadgeCount={meldingenBadgeCount}
        notificationCount={notificationCount}
        showNotificationPopup={showNotificationPopup}
        onToggleNotificationPopup={() => setShowNotificationPopup(!showNotificationPopup)}
        voiceSystem={voiceSystem}
        startVoiceRecognition={startVoiceRecognition}
        stopVoiceRecognition={stopVoiceRecognition}
        onLogout={handleLogout}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
        userProfile={userProfile}
        user={user}
        navigationContext={headerNavigationContext}
      />

      {demoModeEnabled && (
        <div style={{
          background: demoShowroomEnabled ? '#7c2d12' : '#92400e',
          color: 'var(--neutral-0)',
          padding: '10px 14px',
          fontSize: 14,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-12)',
          flexWrap: 'wrap'
        }}>
          <span>
            Demonstratiemodus actief - app draait in presentatiecontext met echte data (write-acties geblokkeerd).
          </span>
          <button
            type="button"
            onClick={() => navigateAndLog('/demo/showroom')}
            style={{
              background: 'var(--neutral-0)',
              color: '#7c2d12',
              border: 'none',
              borderRadius: 8,
              padding: '6px 10px',
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            Open showroom
          </button>
        </div>
      )}

      {showPermissionPrompt && (
        <PermissionPrompt
          requestingPermissions={requestingPermissions}
          onRequestPermissions={requestAppPermissions}
          onDismiss={dismissPermissionsPrompt}
        />
      )}

      <AndroidUpdatePrompt
        androidUpdateInfo={androidUpdateInfo}
        nativeAndroidVersion={nativeAndroidVersion}
        currentAndroidVersion={CURRENT_ANDROID_VERSION}
        autoUpdateStatus={autoUpdateStatus}
        autoUpdateInProgress={autoUpdateInProgress}
        onInstall={() => {
          void installAndroidUpdate(androidUpdateInfo);
        }}
        onDismiss={() => {
          if (androidUpdateInfo?.dismissedKey) {
            localStorage.setItem(androidUpdateInfo.dismissedKey, '1');
          }
          setAndroidUpdateInfo(null);
        }}
      />

      <ChangelogUpdatesPrompt
        updateInfo={changelogUpdateInfo}
        onLater={markChangelogUpdatesLater}
        onApply={applyChangelogUpdate}
      />

      <NotificationPopup
        open={showNotificationPopup}
        notifications={notifications}
        gelezenNotificaties={gelezenNotificaties}
        setGelezenNotificaties={setGelezenNotificaties}
        onNavigate={navigateAndLog}
        onClose={() => setShowNotificationPopup(false)}
        onMarkAllRead={markAllNotificationsAsRead}
      />
        </>
      )}

      <main
        className="main-content"
        key={`route-main-${routeContentKey}`}
        data-route-path={location.pathname}
        data-route-key={routeContentKey}
      >
        <div className="app-content" key={`route-content-${routeContentKey}`}>
          <Suspense fallback={<LoadingScreen />}>
            <AppRoutes
              key={`app-routes-${routeContentKey}`}
            authLoading={authLoading}
            user={userWithRoleCapabilities}
            approved={approved}
            appDisplayVersion={CURRENT_ANDROID_VERSION}
            rolesLoaded={rolesLoaded}
            userBranchId={effectiveUserBranchId}
            isSuperAdmin={effectiveIsSuperAdmin}
            meldingen={meldingen}
            onMeldingenLoaded={syncAdminPanelMeldingen}
            analyticsMeldingen={analyticsData.meldingen}
            analyticsPerformanceRecords={location.pathname === '/analytics/rpd-manager' || location.pathname === '/analytics/dossier' ? [] : (analyticsData.performanceRecords || analyticsData.meldingen)}
            analyticsRobots={analyticsData.robots}
            analyticsGroups={analyticsData.groups || []}
            analyticsGroupsStatus={analyticsData.groupsStatus || 'idle'}
            analyticsGroupsError={analyticsData.groupsError || null}
            analyticsResolvedBranchId={analyticsData.resolvedBranchId || effectiveUserBranchId}
            performanceNorms={analyticsData.performanceNorms || []}
            analyticsStatus={analyticsState.status}
            analyticsError={analyticsState.error}
            analyticsManualReview={analyticsData.manualReview || []}
            analyticsCounts={analyticsData.analyticsCounts || {}}
            analyticsDiagnostics={{
              ...analyticsData.diagnostics,
              sourceRecords: analyticsData.sourceRecords,
              manualReview: (analyticsData.manualReview || []).length,
            }}
            loadSelectedRobotPerformance={loadSelectedRobotPerformance}
            homeOverviewStats={homeOverviewStats}
            filteredMeldingen={filteredMeldingen}
            weekTechnician={weekTechnician}
            loadingWeekTechnician={loadingWeekTechnician}
            setWeekTechnician={setWeekTechnician}
            getDisplayName={getDisplayName}
            addMelding={addMelding}
            deleteMelding={deleteMelding}
            isAdmin={isAdmin}
            showToast={showToast}
            playSuccess={playSuccess}
            playError={playError}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            gemeenteFilter={gemeenteFilter}
            setGemeenteFilter={setGemeenteFilter}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            robotSearch={robotSearch}
            setRobotSearch={setRobotSearch}
            clearAllFilters={clearAllFilters}
            exportFilteredData={exportFilteredData}
            meldingRefreshTrigger={meldingRefreshTrigger}
            onderdelen={onderdelen}
            magazijnVoorraad={magazijnVoorraad}
            notificationsEnabled={notificationsEnabled}
            notificationPreferences={notificationPreferences}
            toggleNotifications={toggleNotifications}
            toggleNotificationPreference={toggleNotificationPreference}
            bijzonderheden={bijzonderheden}
            garanties={garanties}
            gelezenNotificaties={gelezenNotificaties}
            markNotificationAsRead={markNotificationAsRead}
            branchToken={branchToken}
            darkMode={darkMode}
            fetchAndroidUpdate={fetchAndroidUpdate}
            downloadLatestAndroidApk={downloadLatestAndroidApk}
            sendTestNotification={sendTestNotification}
            requestAppPermissions={requestAppPermissions}
            refreshPermissionStatus={refreshPermissionStatus}
            permissionStatus={permissionStatus}
            androidUpdateInfo={androidUpdateInfo}
            autoUpdateInProgress={autoUpdateInProgress}
            autoUpdateStatus={autoUpdateStatus}
            demoMode={{ enabled: demoModeEnabled, showroom: demoShowroomEnabled }}
            userProfile={userProfile}
              updateDemoModeSettings={updateDemoModeSettings}
            />
          </Suspense>
        </div>
      </main>

      {/* Route debug overlay (toggle with localStorage.routeDebugOverlay = '1') */}
      {typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('routeDebugOverlay') === '1' && (
        <RouteDebugOverlay />
      )}

      {/* Toast notifications */}
      <MaintenanceGroundTruthModal
        open={maintenanceGroundTruthState.open}
        robotLabel={maintenanceGroundTruthState.context?.robotLabel}
        robotSerial={maintenanceGroundTruthState.context?.robotSerial}
        headNames={maintenanceGroundTruthState.context?.headNames || []}
        predictedByHead={maintenanceGroundTruthState.context?.predictedByHead || {}}
        saving={maintenanceGroundTruthState.saving}
        onCancel={closeMaintenanceGroundTruthModal}
        onSkip={skipMaintenanceGroundTruth}
        onSave={saveMaintenanceGroundTruthAndClose}
      />
      <Toast />

      </div>
    </BranchProvider>
  );
}

function App() {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';

  if (process.env.NODE_ENV !== 'production' && normalizedPath === '/__dev/toast-visual-qa') {
    return <ToastVisualQaHarness />;
  }

  // Keep the public intake outside the authenticated app entirely. This means
  // auth initialization, user-doc loading, approval checks, and login fallback
  // cannot intercept /tickets or /tickets/ (query strings remain supported).
  if (normalizedPath === '/tickets') return <PublicTicketsPage />;

  return <AuthenticatedApp />;
}

export default App;
